import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createRoot, type Root } from "react-dom/client";
import type {
  InlineTagRenderInfo,
  MentionTag,
  XAdkSenderInputPart,
  XAdkSenderSubmitPart,
} from "@/types";
import { useStyles } from "./styles";
import { parseMarkdownResourceLinksAsParts } from "@/utils/markdownResource";

export interface InlineTagInputRef {
  focus: () => void;
  getText: () => string;
  getParts: () => XAdkSenderSubmitPart[];
  getCursorOffset: () => number;
  insertMention: (
    mention: MentionTag,
    replace?: { start: number; end: number },
  ) => void;
  /** @deprecated 请使用 insertMention。 */
  insertTag: (
    mention: MentionTag,
    replace?: { start: number; end: number },
  ) => void;
}

export interface InlineTagInputProps {
  value: string;
  mentions: MentionTag[];
  defaultInputParts?: XAdkSenderInputPart[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onMentionsChange: (mentions: MentionTag[]) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLDivElement>;
  renderMention?: (
    mention: MentionTag,
    info: InlineTagRenderInfo,
  ) => React.ReactNode;
}

const MENTION_ATTR = "data-xadk-inline-mention-id";
// contenteditable 无法把光标放进 contenteditable=false 的 Mention 里，需用零宽字符承接“Mention 后方”的光标。
const CARET_TEXT = "\u200B";

const createTextNode = (text: string) => document.createTextNode(text);
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;
const normalizeText = (text: string) =>
  text.replace(/\u200B/g, "").replace(/\u00a0/g, " ");
const getPlainLength = (text: string) => normalizeText(text).length;
// DOM 文本里包含零宽光标锚点，快捷键替换和光标定位都要在“用户可见文本 offset”和 DOM raw offset 之间转换。
const toRawOffset = (text: string, plainOffset: number) => {
  let current = 0;
  for (let i = 0; i < text.length; i++) {
    if (current >= plainOffset) return i;
    if (text[i] !== CARET_TEXT) current += 1;
  }
  return text.length;
};

// 内联标签展示逻辑
const InlineTagInput = forwardRef<InlineTagInputRef, InlineTagInputProps>(
  (
    {
      value,
      mentions,
      defaultInputParts,
      placeholder,
      disabled,
      onChange,
      onMentionsChange,
      onKeyDown,
      onKeyUp,
      renderMention,
    },
    ref,
  ) => {
    const styles = useStyles();
    const editorRef = useRef<HTMLDivElement>(null);
    const latestMentionsRef = useRef<MentionTag[]>(mentions);
    const tagRootMapRef = useRef<WeakMap<Element, Root>>(new WeakMap());
    const composingRef = useRef(false);
    const lastInsertedCaretRef = useRef<Text | null>(null);
    const savedRangeRef = useRef<Range | null>(null);
    const defaultInputPartsAppliedRef = useRef(false);
    const skipEmptyCleanupOnceRef = useRef(false);
    const structuralCleanupFrameRef = useRef<number | null>(null);
    const [hasContent, setHasContent] = useState(() =>
      Boolean(value || mentions.length),
    );
    const lastEmittedStateRef = useRef({
      value,
      mentionIds: mentions.map((mention) => mention.id).join("|"),
    });

    useEffect(() => {
      latestMentionsRef.current = mentions;
    }, [mentions]);

    const getOrderedMentions = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return [];
      const mentionMap = new Map(
        latestMentionsRef.current.map((mention) => [mention.id, mention]),
      );
      return Array.from(editor.querySelectorAll(`[${MENTION_ATTR}]`))
        .map((node) =>
          mentionMap.get(
            (node as HTMLElement).dataset.xadkInlineMentionId || "",
          ),
        )
        .filter(Boolean) as MentionTag[];
    }, []);

    // 对外只暴露用户输入的纯文本，内部零宽锚点不进入 value。
    const getPlainText = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return "";
      let text = "";
      editor.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          text += node.textContent || "";
        }
      });
      return normalizeText(text);
    }, []);

    // 提交时需要保留文本与 Mention 的真实顺序；仅靠 value + mentions 无法区分 Mention 在句首、句中还是句尾。
    const getParts = useCallback((): XAdkSenderSubmitPart[] => {
      const editor = editorRef.current;
      if (!editor) return [];
      const mentionMap = new Map(
        latestMentionsRef.current.map((mention) => [mention.id, mention]),
      );
      const parts: XAdkSenderSubmitPart[] = [];

      editor.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = normalizeText(node.textContent || "");
          if (text) parts.push({ type: "text", text });
          return;
        }

        if (node instanceof HTMLElement && node.hasAttribute(MENTION_ATTR)) {
          const mentionId = node.dataset.xadkInlineMentionId || "";
          const mention = mentionMap.get(mentionId);
          if (mention) parts.push({ type: "mention", mention });
        }
      });

      return parts;
    }, []);

    const emitChange = useCallback(() => {
      const nextValue = getPlainText();
      const nextMentions = getOrderedMentions();
      setHasContent(Boolean(nextValue || nextMentions.length));
      lastEmittedStateRef.current = {
        value: nextValue,
        mentionIds: nextMentions.map((mention) => mention.id).join("|"),
      };
      onChange(nextValue);
      onMentionsChange(nextMentions);
    }, [getOrderedMentions, getPlainText, onChange, onMentionsChange]);

    const removeTextRange = useCallback(
      (node: Text, start: number, end: number) => {
        const text = node.textContent || "";
        node.textContent = `${text.slice(0, start)}${text.slice(end)}`;
        if (!node.textContent) node.remove();
      },
      [],
    );

    const removeMentionNode = useCallback(
      (node: Element) => {
        const previous = node.previousSibling;
        const next = node.nextSibling;

        tagRootMapRef.current.get(node)?.unmount();
        node.remove();

        // Mention 后会补一个不可见光标锚点；删除 Mention 时同步清理，避免留下冗余节点。
        if (next?.nodeType === Node.TEXT_NODE) {
          const text = next.textContent || "";
          const caretLength = text.match(/^\u200B+/)?.[0].length || 0;
          if (caretLength > 0) {
            removeTextRange(next as Text, 0, caretLength);
            return;
          }
        }

        if (previous?.nodeType === Node.TEXT_NODE) {
          const text = previous.textContent || "";
          const spaceLength = text.match(/\s+$/)?.[0].length || 0;
          if (spaceLength > 0) {
            removeTextRange(
              previous as Text,
              text.length - spaceLength,
              text.length,
            );
          }
        }
      },
      [removeTextRange],
    );

    const removeMentionById = useCallback(
      (id: string) => {
        const editor = editorRef.current;
        const node = Array.from(
          editor?.querySelectorAll(`[${MENTION_ATTR}]`) || [],
        ).find(
          (item) => (item as HTMLElement).dataset.xadkInlineMentionId === id,
        );
        if (node) {
          removeMentionNode(node);
        }
        emitChange();
        editor?.focus();
      },
      [emitChange, removeMentionNode],
    );

    // Mention 节点本身不可编辑；删除入口通过 onRemove 回到组件内部统一维护 DOM 与 mentions。
    const buildMentionNode = useCallback(
      (mention: MentionTag) => {
        const span = document.createElement("span");
        span.setAttribute("contenteditable", "false");
        span.setAttribute(MENTION_ATTR, mention.id);
        span.dataset.xadkInlineMentionId = mention.id;
        span.dataset.xadkInlineMentionType = mention.type;
        span.className = styles.inlineTag;

        if (renderMention) {
          const root = createRoot(span);
          tagRootMapRef.current.set(span, root);
          root.render(
            <>
              {renderMention(mention, {
                onRemove: () => removeMentionById(mention.id),
              })}
            </>,
          );
        } else {
          const label = document.createElement("span");
          label.className = "xadk-inline-tag-label";
          label.textContent = mention.displayName || mention.label;
          const close = document.createElement("span");
          close.className = "xadk-inline-tag-close";
          close.innerHTML = "×";
          close.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            removeMentionById(mention.id);
          };
          span.append(label, close);
        }
        return span;
      },
      [removeMentionById, renderMention, styles.inlineTag],
    );

    const renderPartsToEditor = useCallback(
      (parts: XAdkSenderInputPart[]) => {
        const editor = editorRef.current;
        if (!editor) return;

        editor.querySelectorAll(`[${MENTION_ATTR}]`).forEach((node) => {
          tagRootMapRef.current.get(node)?.unmount();
        });
        editor.innerHTML = "";

        parts.forEach((part) => {
          if (part.type === "text") {
            if (part.text) editor.appendChild(createTextNode(part.text));
            return;
          }
          const mention = part.type === "mention" ? part.mention : part.tag;
          editor.append(buildMentionNode(mention), createTextNode(CARET_TEXT));
        });
      },
      [buildMentionNode],
    );

    const setCursorAtTextEnd = useCallback((node: Text) => {
      const range = document.createRange();
      const selection = window.getSelection();
      range.setStart(node, node.textContent?.length || 0);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
      savedRangeRef.current = range.cloneRange();
    }, []);

    const saveSelectionRange = useCallback(() => {
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor || !selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (
        !editor.contains(range.startContainer) ||
        !editor.contains(range.endContainer)
      ) {
        return;
      }
      savedRangeRef.current = range.cloneRange();
    }, []);

    const restoreCursorAfterInsert = useCallback(
      (node: Text) => {
        const editor = editorRef.current;
        if (!editor || !editor.contains(node)) return;
        editor.focus();
        setCursorAtTextEnd(node);
      },
      [setCursorAtTextEnd],
    );

    const isMentionNode = (node: Node | null): node is HTMLElement =>
      node instanceof HTMLElement && node.hasAttribute(MENTION_ATTR);

    const isOnlyCaretText = (text: string) => normalizeText(text) === "";

    // Backspace/Delete 需要跨过零宽锚点识别相邻 Mention，不能只看光标所在文本节点。
    const getMentionBeforeRange = (range: Range) => {
      const container = range.startContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        const text = container.textContent || "";
        const beforeCursor = text.slice(0, range.startOffset);
        if (
          isOnlyCaretText(beforeCursor) &&
          isMentionNode(container.previousSibling)
        ) {
          return container.previousSibling;
        }
      }

      if (container instanceof HTMLElement) {
        const previous = container.childNodes[range.startOffset - 1];
        if (isMentionNode(previous)) return previous;
        if (
          previous?.nodeType === Node.TEXT_NODE &&
          isOnlyCaretText(previous.textContent || "") &&
          isMentionNode(previous.previousSibling)
        ) {
          return previous.previousSibling;
        }
      }

      return null;
    };

    const getMentionAfterRange = (range: Range) => {
      const container = range.startContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        const text = container.textContent || "";
        const afterCursor = text.slice(range.startOffset);
        if (
          isOnlyCaretText(afterCursor) &&
          isMentionNode(container.nextSibling)
        ) {
          return container.nextSibling;
        }
      }

      if (container instanceof HTMLElement) {
        const next = container.childNodes[range.startOffset];
        if (isMentionNode(next)) return next;
        if (
          next?.nodeType === Node.TEXT_NODE &&
          isOnlyCaretText(next.textContent || "") &&
          isMentionNode(next.nextSibling)
        ) {
          return next.nextSibling;
        }
      }

      return null;
    };

    const moveCursorAroundMention = useCallback(
      (mention: HTMLElement, direction: "before" | "after") => {
        const editor = editorRef.current;
        const selection = window.getSelection();
        if (!editor || !selection || !editor.contains(mention)) return;

        const range = document.createRange();
        if (direction === "before") {
          const previous = mention.previousSibling;
          if (previous?.nodeType === Node.TEXT_NODE) {
            range.setStart(previous, previous.textContent?.length || 0);
          } else {
            range.setStartBefore(mention);
          }
        } else {
          const next = mention.nextSibling;
          if (next?.nodeType === Node.TEXT_NODE) {
            const leadingCaretLength =
              (next.textContent || "").match(/^\u200B+/)?.[0].length || 0;
            range.setStart(next, leadingCaretLength);
          } else {
            range.setStartAfter(mention);
          }
        }

        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        savedRangeRef.current = range.cloneRange();
      },
      [],
    );

    // 读取光标在纯文本中的位置，供快捷键搜索词和 Mention 插入位置复用。
    const getOffsetInEditor = useCallback(() => {
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor || !selection || selection.rangeCount === 0) return 0;
      const range = selection.getRangeAt(0);
      let offset = 0;
      let found = false;

      const visit = (node: Node) => {
        if (found) return;
        if (node === range.startContainer) {
          if (node.nodeType === Node.TEXT_NODE) {
            offset += getPlainLength(
              (node.textContent || "").slice(0, range.startOffset),
            );
          }
          found = true;
          return;
        }
        if (node instanceof HTMLElement && node.hasAttribute(MENTION_ATTR))
          return;
        if (node.nodeType === Node.TEXT_NODE) {
          offset += getPlainLength(node.textContent || "");
          return;
        }
        node.childNodes.forEach(visit);
      };

      editor.childNodes.forEach(visit);
      return offset;
    }, []);

    const getNodeIndex = (node: Node) => {
      let index = 0;
      let current = node;
      while (current.previousSibling) {
        index += 1;
        current = current.previousSibling;
      }
      return index;
    };

    // 删除快捷键触发片段，例如把 “@文档” 替换成一个结构化 Mention，并返回删除位置对应的真实 DOM Range。
    // 注意：纯文本 offset 不包含 Mention，Mention 前后可能都是 offset 0；插入 Mention 必须依赖真实 Range，不能再反推 offset。
    const replaceTextRange = useCallback((start: number, end: number) => {
      const editor = editorRef.current;
      if (!editor) return null;
      let offset = 0;
      let insertRange: Range | null = null;
      const nodes = Array.from(editor.childNodes);
      nodes.forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;
        const text = node.textContent || "";
        const nodeStart = offset;
        const plainLength = getPlainLength(text);
        const nodeEnd = offset + plainLength;
        offset = nodeEnd;
        if (nodeEnd <= start || nodeStart >= end) return;
        const rawStart = toRawOffset(text, Math.max(0, start - nodeStart));
        const rawEnd = toRawOffset(text, Math.max(0, end - nodeStart));
        const keepLeft = text.slice(0, rawStart);
        const keepRight = text.slice(rawEnd);
        const parent = node.parentNode;
        const nodeIndex = getNodeIndex(node);

        node.textContent = `${keepLeft}${keepRight}`;
        if (!insertRange) {
          insertRange = document.createRange();
          if (node.textContent) {
            insertRange.setStart(node, rawStart);
          } else if (parent) {
            insertRange.setStart(parent, nodeIndex);
          }
          insertRange.collapse(true);
        }
        if (!node.textContent) node.remove();
      });

      return insertRange;
    }, []);

    // 浏览器拆分 Range 后可能留下空文本节点；清理后才能保证后续 Mention 继续 append 在队尾。
    const cleanupCaretTextNodes = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return;
      Array.from(editor.childNodes).forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;
        const text = node.textContent || "";
        if (!text) {
          node.remove();
          return;
        }
        if (text && normalizeText(text) === "") {
          node.textContent = CARET_TEXT;
        }
      });
    }, []);

    // Chrome 会把标签前临时输入的文字包进块级节点；文字被删空后，可能留下
    // <div><br></div>。它没有消息语义，却会在 contenteditable 中占据一整行。
    const cleanupEmptyStructuralNodes = useCallback(() => {
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor) return;

      Array.from(editor.children).forEach((node) => {
        if (node.hasAttribute(MENTION_ATTR)) return;
        if (node.querySelector(`[${MENTION_ATTR}]`)) return;
        if (normalizeText(node.textContent || "")) return;

        const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
        const nodeIndex = getNodeIndex(node);
        const containsCursor = Boolean(
          range &&
          range.collapsed &&
          (range.startContainer === node ||
            node.contains(range.startContainer) ||
            (range.startContainer === editor &&
              (range.startOffset === nodeIndex ||
                range.startOffset === nodeIndex + 1))),
        );

        if (containsCursor && selection) {
          // 光标紧邻 contenteditable=false 的 Mention 时，单纯删除 <br> 会让 Chrome
          // 再次生成它。替换成零宽文本锚点，既能承接光标，也不会增加行高。
          const caretNode = createTextNode(CARET_TEXT);
          node.replaceWith(caretNode);
          const nextRange = document.createRange();
          nextRange.setStart(caretNode, CARET_TEXT.length);
          nextRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(nextRange);
          savedRangeRef.current = nextRange.cloneRange();
          return;
        }

        node.remove();
      });
    }, []);

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;

      // 某些业务容器会截断 React 委托事件；直接监听编辑器的原生 input，确保删除事务结束后
      // 仍能清理 Chrome 在不可编辑 Mention 前生成的占行 <br>。仅处理 delete*，不影响 Enter 换行。
      const handleNativeDelete = (event: Event) => {
        if (!(event instanceof InputEvent)) return;
        if (!event.inputType.startsWith("delete")) return;
        if (structuralCleanupFrameRef.current !== null) {
          window.cancelAnimationFrame(structuralCleanupFrameRef.current);
        }
        structuralCleanupFrameRef.current = window.requestAnimationFrame(() => {
          structuralCleanupFrameRef.current = null;
          cleanupEmptyStructuralNodes();
          cleanupCaretTextNodes();
        });
      };

      editor.addEventListener("input", handleNativeDelete);
      return () => editor.removeEventListener("input", handleNativeDelete);
    }, [cleanupCaretTextNodes, cleanupEmptyStructuralNodes]);

    const insertMention = useCallback(
      (mention: MentionTag, replace?: { start: number; end: number }) => {
        const editor = editorRef.current;
        if (!editor) return;
        // 工具栏/弹窗会先抢走焦点；必须在 focus 之前保存当前 Selection，
        // 否则浏览器新建在输入框开头的 Range 会覆盖最后一次真实光标位置。
        const selectionBeforeFocus = window.getSelection();
        const activeRangeBeforeFocus =
          selectionBeforeFocus?.rangeCount &&
          editor.contains(selectionBeforeFocus.getRangeAt(0).startContainer)
            ? selectionBeforeFocus.getRangeAt(0).cloneRange()
            : null;
        const savedRangeBeforeFocus =
          savedRangeRef.current &&
          editor.contains(savedRangeRef.current.startContainer)
            ? savedRangeRef.current.cloneRange()
            : null;
        const rangeBeforeFocus =
          activeRangeBeforeFocus || savedRangeBeforeFocus;

        editor.focus();
        latestMentionsRef.current = [
          ...latestMentionsRef.current.filter((item) => item.id !== mention.id),
          mention,
        ];
        let insertRange: Range | null = null;
        if (replace) {
          insertRange = replaceTextRange(replace.start, replace.end);
          cleanupCaretTextNodes();
        }
        // 浏览器会在空 contenteditable 中生成 <div><br> 等占位块。
        // 空编辑器插入首个 Mention 前先清掉这些无语义节点，避免标签被放进第二个视觉行。
        const isSemanticallyEmpty =
          !getPlainText() && !editor.querySelector(`[${MENTION_ATTR}]`);
        if (isSemanticallyEmpty) {
          editor.innerHTML = "";
          savedRangeRef.current = null;
          insertRange = null;
        }
        const mentionNode = buildMentionNode(mention);
        const caretNode = createTextNode(CARET_TEXT);
        const range = insertRange || rangeBeforeFocus;
        if (range && editor.contains(range.startContainer)) {
          // 用 DocumentFragment 一次性插入，避免连续 insertNode 导致 mention/caret 顺序被浏览器 Range 状态打乱。
          const fragment = document.createDocumentFragment();
          fragment.append(mentionNode, caretNode);
          range.insertNode(fragment);
        } else {
          editor.append(mentionNode, caretNode);
        }
        cleanupCaretTextNodes();
        lastInsertedCaretRef.current = caretNode;
        setCursorAtTextEnd(caretNode);
        emitChange();

        // 关闭快捷列表和父组件同步 mentions/value 会触发一次 React 更新；
        // 下一帧再恢复到刚插入 Mention 后的零宽锚点，避免光标被 focus 或重渲染带回旧位置。
        window.requestAnimationFrame(() => {
          if (lastInsertedCaretRef.current === caretNode) {
            restoreCursorAfterInsert(caretNode);
          }
        });
      },
      [
        buildMentionNode,
        cleanupCaretTextNodes,
        emitChange,
        getPlainText,
        replaceTextRange,
        restoreCursorAfterInsert,
        setCursorAtTextEnd,
      ],
    );

    useEffect(() => {
      document.addEventListener("selectionchange", saveSelectionRange);
      return () => {
        document.removeEventListener("selectionchange", saveSelectionRange);
      };
    }, [saveSelectionRange]);

    useEffect(
      () => () => {
        if (structuralCleanupFrameRef.current !== null) {
          window.cancelAnimationFrame(structuralCleanupFrameRef.current);
        }
      },
      [],
    );

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editorRef.current?.focus(),
        getText: getPlainText,
        getParts,
        getCursorOffset: getOffsetInEditor,
        insertMention,
        insertTag: insertMention,
      }),
      [getOffsetInEditor, getParts, getPlainText, insertMention],
    );

    useIsomorphicLayoutEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;

      // 默认片段只在空输入首次挂载时灌入，避免外部重新渲染覆盖用户正在编辑的内容。
      if (
        !defaultInputPartsAppliedRef.current &&
        defaultInputParts?.length &&
        !value &&
        mentions.length === 0
      ) {
        defaultInputPartsAppliedRef.current = true;
        // emitChange 的受控回写要到下一次 render 才生效；跳过本轮紧随其后的空值清理，
        // 避免刚渲染的默认 Mention 被旧的空 value/mentions 立即移除。
        skipEmptyCleanupOnceRef.current = true;
        renderPartsToEditor(defaultInputParts);
        emitChange();
        return;
      }

      const incomingMentionIds = mentions
        .map((mention) => mention.id)
        .join("|");
      const lastEmittedState = lastEmittedStateRef.current;
      const isInternalControlledEcho =
        value === lastEmittedState.value &&
        incomingMentionIds === lastEmittedState.mentionIds;

      if (isInternalControlledEcho && editor.childNodes.length > 0) return;

      renderPartsToEditor([
        ...(value ? [{ type: "text" as const, text: value }] : []),
        ...mentions.map((mention) => ({ type: "mention" as const, mention })),
      ]);
      setHasContent(Boolean(value || mentions.length));

      // 外部主动写入 value/mentions 后，需要在首帧绘制前同步 DOM；若输入区已有焦点，则把光标放在内容末尾。
      const lastTextNode = Array.from(editor.childNodes)
        .reverse()
        .find((node): node is Text => node.nodeType === Node.TEXT_NODE);
      if (document.activeElement === editor && lastTextNode) {
        setCursorAtTextEnd(lastTextNode);
      }
    }, [
      defaultInputParts,
      emitChange,
      renderPartsToEditor,
      setCursorAtTextEnd,
      mentions,
      value,
    ]);

    useIsomorphicLayoutEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      if (skipEmptyCleanupOnceRef.current) {
        skipEmptyCleanupOnceRef.current = false;
        return;
      }
      if (!value && mentions.length === 0 && editor.childNodes.length > 0) {
        editor.querySelectorAll(`[${MENTION_ATTR}]`).forEach((node) => {
          tagRootMapRef.current.get(node)?.unmount();
        });
        editor.innerHTML = "";
      }
    }, [mentions.length, value]);

    const handleInput = () => {
      if (!composingRef.current) {
        cleanupEmptyStructuralNodes();
        cleanupCaretTextNodes();
        emitChange();

        // Chrome 可能在 input 回调返回后才补上用于承接光标的 <br>；下一帧再归一化一次。
        if (structuralCleanupFrameRef.current !== null) {
          window.cancelAnimationFrame(structuralCleanupFrameRef.current);
        }
        structuralCleanupFrameRef.current = window.requestAnimationFrame(() => {
          structuralCleanupFrameRef.current = null;
          cleanupEmptyStructuralNodes();
          cleanupCaretTextNodes();
        });
      }
    };

    const handlePaste: React.ClipboardEventHandler<HTMLDivElement> = (
      event,
    ) => {
      event.preventDefault();
      const editor = editorRef.current;
      if (!editor) return;

      // 始终按 text/plain 粘贴，避免消息气泡的背景色等 HTML 样式进入 contenteditable。
      const text = event.clipboardData.getData("text/plain");
      if (!text) return;
      const parts = parseMarkdownResourceLinksAsParts(text);
      const selection = window.getSelection();
      const range =
        selection?.rangeCount &&
        editor.contains(selection.getRangeAt(0).startContainer)
          ? selection.getRangeAt(0)
          : null;
      const fragment = document.createDocumentFragment();
      let lastTextNode: Text | null = null;

      for (const part of parts) {
        if (part.type === "text") {
          lastTextNode = createTextNode(part.text);
          fragment.append(lastTextNode);
          continue;
        }
        latestMentionsRef.current = [
          ...latestMentionsRef.current,
          part.mention,
        ];
        fragment.append(buildMentionNode(part.mention));
        lastTextNode = createTextNode(CARET_TEXT);
        fragment.append(lastTextNode);
      }

      if (range) {
        range.deleteContents();
        range.insertNode(fragment);
      } else {
        editor.append(fragment);
      }
      cleanupCaretTextNodes();
      if (lastTextNode) setCursorAtTextEnd(lastTextNode);
      emitChange();
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (
      event,
    ) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const selection = window.getSelection();
        if (selection?.rangeCount) {
          const range = selection.getRangeAt(0);
          if (range.collapsed) {
            const targetMention =
              event.key === "ArrowLeft"
                ? getMentionBeforeRange(range)
                : getMentionAfterRange(range);

            if (targetMention) {
              event.preventDefault();
              moveCursorAroundMention(
                targetMention,
                event.key === "ArrowLeft" ? "before" : "after",
              );
              return;
            }
          }
        }
      }

      if (event.key === "Backspace" || event.key === "Delete") {
        const selection = window.getSelection();
        if (selection?.rangeCount) {
          const range = selection.getRangeAt(0);
          if (range.collapsed) {
            const targetMention =
              event.key === "Backspace"
                ? getMentionBeforeRange(range)
                : getMentionAfterRange(range);

            if (targetMention) {
              event.preventDefault();
              removeMentionById(
                targetMention.dataset.xadkInlineMentionId || "",
              );
              return;
            }
          }
        }
      }
      onKeyDown?.(event);
    };

    // placeholder 只由受控 value/mentions 派生，避免从 contenteditable DOM 读取导致外部插入 Mention 后状态滞后一帧。
    const showPlaceholder = !hasContent;

    return (
      <div className={styles.inlineInputWrapper}>
        {showPlaceholder && placeholder && (
          <div className={styles.inlinePlaceholder}>{placeholder}</div>
        )}
        <div
          ref={editorRef}
          className={styles.inlineInput}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          onInput={handleInput}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onKeyUp={(event) => {
            cleanupEmptyStructuralNodes();
            cleanupCaretTextNodes();
            saveSelectionRange();
            onKeyUp?.(event);
          }}
          onMouseUp={saveSelectionRange}
          onFocus={saveSelectionRange}
          onCompositionStart={() => {
            composingRef.current = true;
            // 中文 IME 在 compositionend 前不会触发受控 value 更新；候选输入一出现就应隐藏 placeholder。
            setHasContent(true);
          }}
          onCompositionEnd={() => {
            composingRef.current = false;
            emitChange();
          }}
          data-cursor-offset={getOffsetInEditor()}
        />
      </div>
    );
  },
);

InlineTagInput.displayName = "InlineTagInput";

export default InlineTagInput;
